import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import FullScreenLoader from "components/FullScreenLoader";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Webcam from "react-webcam";
import { usePutOperationMutation } from "redux/api/operationApi";
import { setCurrentOperation } from "redux/features/operationSlice";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch"; // Icono MUI
import { useIntl } from "react-intl";


const DocumentReverseCapture: React.FC<{
  nextStep: () => void;
  previousStep: () => void;
}> = ({ nextStep, previousStep }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [
    updateOperation,
    {
      data: postedOperationData,
      isSuccess: isPostedOperationSuccess,
      isLoading: loadingOperation,
      error: apiError,
    },
  ] = usePutOperationMutation();
  const IMAGE_SERVER = process.env.REACT_APP_IMAGES_ENDPOINT;

  const webcamRef = useRef<Webcam>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment"
  );

  // Acceso a estado global
  const operation = useSelector(
    (state: any) =>
      state.operationState?.currentOperation?.data ||
      state.operationState?.currentOperation
  );
  const documentTypeName = useSelector(
    (state: any) => state.operationState?.documentTypeName
  );

  // 🔹 Asegurar resolución de 1920x1080
  const videoConstraints = {
    facingMode,
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  };
  useEffect(() => {
    if (operation?.documentReverse) {
      setImgSrc(`${IMAGE_SERVER}/${operation.documentReverse}`);
    }
  }, []);

  useEffect(() => {
    if (isPostedOperationSuccess && postedOperationData) {
      const operationData =
        "data" in postedOperationData
          ? postedOperationData.data
          : postedOperationData;
      dispatch(setCurrentOperation(operationData));
      nextStep();
    }
  }, [isPostedOperationSuccess]);

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  // 🔹 Capturar imagen en resolución máxima usando canvas
  const capture = useCallback(() => {
    if (webcamRef.current) {
      const video = webcamRef.current.video;
      if (video) {
        const aspectRatio = video.videoWidth / video.videoHeight;
        const canvas = document.createElement("canvas");
        canvas.width = 1080 * aspectRatio;
        canvas.height = 1080;
        const ctx = canvas.getContext("2d");

        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageSrc = canvas.toDataURL("image/png", 1.0); // Calidad máxima
          setImgSrc(imageSrc);
        }
      }
    }
  }, [webcamRef]);

  const retakeImage = () => {
    setImgSrc(null);
    setError(false);
  };

  const handleSubmit = async () => {
    if (!imgSrc) {
      setError(true);
      return;
    }
    if (IMAGE_SERVER && imgSrc.startsWith(IMAGE_SERVER)) {
      nextStep();
      return;
    }
    const imageBlob = await fetch(imgSrc).then((res) => res.blob());
    const formData = new FormData();
    const operationData = {
      ...operation,
      documentReverse: imgSrc,
      mrzChecked: false,
    };
    formData.append("data", JSON.stringify(operationData));
    formData.append("documentReverse", imageBlob, `webcam-${Date.now()}.png`);
    updateOperation({ id: operation.id, operationToUpdate: formData });
  };

  if (loadingOperation) return <FullScreenLoader />;

  return (
    <Card sx={{ p: 2 }}>
      <CardHeader
        title={
          <Typography variant="h1" align="center">
            {documentTypeName === "Passport"
              ? formatMessage({ id: "document.passport.title" })
              : formatMessage({ id: `document.reverse.title.${documentTypeName}` })}
          </Typography>
        }
      />
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body2" align="center" color="textSecondary">
            {formatMessage({ id: "document.instructions" })}            
          </Typography>
          <FormControl error={error} fullWidth>
            {!imgSrc ? (
              <Box sx={{ position: "relative", width: "100%" }}>
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/png"
                  videoConstraints={videoConstraints}
                  style={{
                    width: "100%",
                    height: "auto",
                    aspectRatio: 3 / 4,
                    borderRadius: "10px",
                    border: "2px solid #ccc",
                  }}
                />
                <CameraswitchIcon
                  onClick={toggleCamera}
                  color="secondary"
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    minWidth: "40px",
                    minHeight: "40px",
                    borderRadius: "50%",
                    padding: "5px",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <img
                  src={imgSrc}
                  alt="Captura de la webcam"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "10px",
                    marginTop: "10px",
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                mt: 2,
              }}
            >
              {!imgSrc ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capture}
                  sx={{ width: "80%", fontSize: "16px", padding: "10px" }}
                >
                  {formatMessage({ id: "buttons.take.picture" })}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={retakeImage}
                  sx={{ width: "80%", fontSize: 16, padding: 1.25 }}
                >
                  {formatMessage({ id: "buttons.take.again" })}
                </Button>
              )}
            </Box>
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              sx={{ mt: 3 }}
            >
              <Grid item xs={12} lg={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={previousStep}
                  sx={{
                    mt: 2,
                    backgroundColor: "#FFF",
                    display: {
                      xs: "none", // Ocultar en pantallas pequeñas (móviles)
                      sm: "none", // Ocultar en tablets
                      md: "block", // Mostrar en pantallas medianas y grandes
                    },
                  }}
                >
                  {formatMessage({ id: "buttons.back" })}
                </Button>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  sx={{ mt: 2, backgroundColor: "black", color: "white" }}
                  disabled={error || !imgSrc}
                >
                  {formatMessage({ id: "buttons.continue" })}
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DocumentReverseCapture;
